import { useEffect, useMemo, useState } from 'react';
import { Typography, Box, IconButton, Button, FormControl, RadioGroup, FormControlLabel, Radio } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'packages/novous_web/src/authContext/useAuth';
import dayjs from "dayjs";
import { ArrowBack } from '@mui/icons-material';
import CustomIconButton from 'packages/novous_web/src/common/components/CustomIconButton';
import eyeIcon from 'packages/novous_web/src/assets/icons/Eye.svg'
import InputField from 'packages/novous_web/src/common/components/InputField';
import searchIcon from 'packages/novous_web/src/assets/icons/search.svg'
import useAxios from 'packages/novous_web/src/api/useAxios';
import { API_ENDPOINTS } from 'packages/novous_web/src/api/endpoints';
import deleteIcon from 'packages/novous_web/src/assets/icons/Delete.svg'
import Buttons from 'packages/novous_web/src/common/components/Button'
import CustomReportPreview from './CustomReportPreview';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import useApiError from 'packages/novous_web/src/api/useApiError';
import AlertToast from 'packages/novous_web/src/common/components/Alert';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import dragIcon from 'packages/novous_web/src/assets/icons/DragIcon.svg'
import DOMPurify from 'dompurify';


interface DateFieldProps {
    label: string;
    name: string;
    value: string | null;
    onChange: (value: string | null) => void;
    isDisabled?: boolean;
    id: string;
}

interface EmailApiResponse {
    data: string;
    error: string;
    message: string;
    status: number;
}

const DateField: React.FC<DateFieldProps> = ({
    label,
    name,
    value,
    onChange,
    isDisabled = false,
}) => {
    return (
        <div className="flex flex-col w-full">
            <Typography variant="body2" fontWeight={600} color="textPrimary">
                {label}
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                    value={value ? dayjs(value, 'DD/MM/YYYY') : null}
                    onChange={(newValue) =>
                        onChange(newValue ? dayjs(newValue).format('DD/MM/YYYY') : null)
                    }
                    format="DD/MM/YYYY"
                    disabled={isDisabled}
                    slotProps={{
                        textField: { fullWidth: true, variant: 'outlined' },
                    }}
                    sx={(theme) => ({
                        width: '100%',
                        marginTop: '8px',
                        '& .MuiOutlinedInput-root': {
                            padding: '12px 16px',
                            fontSize: '14px',
                            fontWeight: 400,
                            borderRadius: '8px',
                            borderWidth: '1px',
                            borderColor: theme.palette.action.selected,
                        },
                        '& .MuiInputBase-input': {
                            padding: '0px',
                            borderWidth: '0px',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                        },
                        '& .MuiSvgIcon-root': {
                            color: theme.palette.primary.main,
                        },
                    })}
                    className={`mt-2 w-full ${isDisabled ? 'bg-gray-200 cursor-not-allowed' : ''
                        }`}
                />
            </LocalizationProvider>
        </div>
    );
};

interface MasterReport {
    reportWiseColumns: {
        All?: any[];
        all?: any[];
    };
    date_time: string;
    reportName: string;
    reportDescription: string;
    reportDisplayName: string;
    reportType: string;
}

const data: MasterReport[] = []

export default function CreateCustomReports() {
    const [alertOpen, setAlertOpen] = useState<boolean>(false);
    const [alertMessage, setAlertMessage] = useState<string>("");
    const [alertType, setAlertType] = useState<"error" | "success" | "warning" | "info">("error");
    const [isSendingEmail, setIsSendingEmail] = useState(false);

    const [reportName, setReportName] = useState('');
    const [reportDescription, setReportDescription] = useState('');
    const [isDateRangeValid, setIsDateRangeValid] = useState(true);

    const navigate = useNavigate();
    const dateRangeArr = [
        "7 Days",
        "15 Days",
        "30 Days",
        "Custom Range",
    ];

    const [selectedDuration, setSelectedDuration] = useState(dateRangeArr[0]);
    const [calendarAnchorEl, setCalendarAnchorEl] = useState<null | HTMLElement>(
        null
    );
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [searchTerm, setSearchTerm] = useState('');

    const getDateRange = (range: string) => {
        const today = dayjs();
        let startDate, endDate;

        switch (range) {
            case "Last 7 Days":
                startDate = dayjs().subtract(6, "day").format("YYYY-MM-DD");
                endDate = today.format("YYYY-MM-DD");
                break;
            case "Last 15 Days":
                startDate = dayjs().subtract(14, "day").format("YYYY-MM-DD");
                endDate = today.format("YYYY-MM-DD");
                break;
            case "Last 30 Days":
                startDate = dayjs().subtract(29, "day").format("YYYY-MM-DD");
                endDate = today.format("YYYY-MM-DD");
                break;
            case "Custom Range":
                // These will be set by the custom inputs
                break;
            default:
                startDate = "";
                endDate = "";
        }
        return { startDate, endDate };
    };

    let { startDate: startDateC, endDate: endDateC } =
        getDateRange(selectedDuration);

    useEffect(() => {
        if (startDateC !== "" && endDateC !== "") {
        }
    }, [startDateC, endDateC]);

    if (startDate !== "" && endDate !== "") {
        startDateC = startDate;
        endDateC = endDate;
    } else if (startDate === "" && endDate === "") {
        ({ startDate: startDateC, endDate: endDateC } =
            getDateRange(selectedDuration));
    }
    useEffect(() => {
        const { startDate: newStartDate = '', endDate: newEndDate = '' } = getDateRange(selectedDuration);
        setStartDate(newStartDate);
        setEndDate(newEndDate);
    }, [selectedDuration]);




    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
        setCalendarAnchorEl(event.currentTarget);
    };

    const handleCalendarClose = () => {
        setShowDatePicker(false);
        setStartDate("");
        setEndDate("");

        setSelectedDuration(dateRangeArr[0]);
        setCalendarAnchorEl(null);
    };

    const handleClose = (value?: string) => {
        console.log("Selected Value:", value);

        if (value) {
            setSelectedDuration(value);
            setShowDatePicker(value === "Custom Range");

            const { startDate: newStartDate = '', endDate: newEndDate = '' } = getDateRange(selectedDuration);
            setStartDate(newStartDate);
            setEndDate(newEndDate);
        }

        setAnchorEl(null);
    };

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const { user } = useAuth();
    const [masterReports, setMasterReports] = useState<MasterReport[]>([]);

    const {
        data: MasterReportData,
        error: MasterReportError,
        loading: MasterReportLoading,
        fetchData: MasterReportRequestData,
    }: { data: any, error: any, loading: boolean, fetchData: any } = useAxios(API_ENDPOINTS.report.MASTER_REPORTS, "POST");

    useEffect(() => {
        MasterReportRequestData({
            fleetId: user.customer_id,
        });
    }, [])

    useEffect(() => {
        if (MasterReportData && MasterReportData.data) {
            setMasterReports(MasterReportData.data);
        }
    }, [MasterReportData]);

    const processedData = useMemo(() => {
        if (!MasterReportData) return [];

        return (MasterReportData.data || []).map((report: any) => {
            return {
                ...report,
            };
        });
    }, [MasterReportData]);

    const [selectedReport, setSelectedReport] = useState<MasterReport | null>(null);
    const [selectedColumns, setSelectedColumns] = useState<Record<string, boolean>>({});
    const [selectAll, setSelectAll] = useState(false);

    const filteredReports = useMemo(() => {
        if (!searchTerm) {
            return processedData.filter((report: MasterReport) =>
                (report.reportName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    report.reportType.toLowerCase().includes(searchTerm.toLowerCase())) &&
                report.reportName !== "vehicledistancerunreport" &&
                report.reportName !== "dailyevent"
            );
        }

        return processedData.filter((report: MasterReport) =>
            report.reportName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            report.reportType.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [processedData, searchTerm]);

    const [isEnabled, setIsEnabled] = useState(false);

    const handleReportSelect = (report: MasterReport) => {
        setSelectedReport(report);
        // Initialize all columns as unchecked
        const initialSelection: Record<string, boolean> = {};
        const columns = report.reportWiseColumns.All || report.reportWiseColumns.all || [];
        columns.forEach((col: any) => {
            // Use col.field if column is an object, otherwise use col directly
            const columnKey = typeof col === 'object' ? col.field : col;
            initialSelection[columnKey] = false;
        });
        setSelectedColumns(initialSelection);
        setSelectAll(false);
    };

    const toggleSelectAll = () => {
        const newSelectAll = !selectAll;
        setSelectAll(newSelectAll);

        if (selectedReport) {
            const columns = selectedReport.reportWiseColumns.All || selectedReport.reportWiseColumns.all || [];
            const updatedSelection: Record<string, boolean> = {};

            columns.forEach((col: any) => {
                const columnKey = typeof col === 'object' ? col.field : col;
                updatedSelection[columnKey] = newSelectAll;
            });

            setSelectedColumns(updatedSelection);

            // Update orderedSelectedColumns
            if (newSelectAll) {
                // If selecting all, add all columns in their original order
                const allFields = columns.map((col: any) => typeof col === 'object' ? col.field : col);
                setOrderedSelectedColumns(allFields);
            } else {
                // If deselecting all, clear the ordered list
                setOrderedSelectedColumns([]);
            }
        }
    };

    // Update the toggleColumn function as well
    const toggleColumn = (columnName: string) => {
        setSelectedColumns(prev => {
            const newSelected = {
                ...prev,
                [columnName]: !prev[columnName]
            };

            // Update the order array when selecting a new column
            if (newSelected[columnName] && !orderedSelectedColumns.includes(columnName)) {
                setOrderedSelectedColumns(prev => [...prev, columnName]);
            } else if (!newSelected[columnName]) {
                // Remove from order when deselecting
                setOrderedSelectedColumns(prev => prev.filter(field => field !== columnName));
            }

            return newSelected;
        });
    };
    const columns = selectedReport?.reportWiseColumns?.All || selectedReport?.reportWiseColumns?.all || [];

    const [columnSearchTerm, setColumnSearchTerm] = useState('');

    const filteredColumns = useMemo(() => {
        if (!columnSearchTerm || !selectedReport) {
            return selectedReport?.reportWiseColumns?.All || selectedReport?.reportWiseColumns?.all || [];
        }

        const columns = selectedReport.reportWiseColumns.All || selectedReport.reportWiseColumns.all || [];
        return columns.filter((column: any) => {
            // Check if column is an object with label property
            const columnText = typeof column === 'object' ? column.label : column;
            return columnText.toLowerCase().includes(columnSearchTerm.toLowerCase());
        });
    }, [columnSearchTerm, selectedReport]);

    const [finalColumnsSearchTerm, setFinalColumnsSearchTerm] = useState('');

    const [isDateRangeModalOpen, setIsDateRangeModalOpen] = useState(false);

    const handleContinue = (e: React.MouseEvent) => {
        e.preventDefault();
        setIsDateRangeModalOpen(true);
    };

    const isContinueDisabled = useMemo(() => {
        return (
            !reportName.trim() ||
            !reportDescription.trim() ||
            Object.values(selectedColumns).filter(Boolean).length === 0
        );
    }, [reportName, reportDescription, selectedColumns]);

    useEffect(() => {
        if (selectedDuration === "Custom Range") {
            const isValid = !!startDate && !!endDate && dayjs(startDate, 'DD/MM/YYYY').isBefore(dayjs(endDate, 'DD/MM/YYYY'));
            setIsDateRangeValid(isValid);
        } else {
            setIsDateRangeValid(true);
        }
    }, [selectedDuration, startDate, endDate]);

    const {
        data: customReportData,
        error: customReportError,
        loading: customReportLoading,
        fetchData: customReportRequestData,
    } = useApiError<EmailApiResponse>(API_ENDPOINTS.report.CUSTOM_REPORT_CREATE, "POST"
    );

    const handleApplyDateRange = async () => {
        setIsSendingEmail(true);
        if (selectedDuration === "Custom Range") {
            const start = dayjs(startDate, 'DD/MM/YYYY');
            const end = dayjs(endDate, 'DD/MM/YYYY');

            if (!startDate || !endDate || start.isAfter(end)) {
                setIsDateRangeValid(false);
                setIsSendingEmail(false);
                return;
            }
        }
        if (!selectedReport) {
            console.error("No report selected");
            return;
        }
        const selectedFields = orderedSelectedColumns.filter(field => selectedColumns[field]);
        const requestData = {
            fleet_id: user.customer_id,
            parent_id: user.parentId,
            reportCategory: selectedReport.reportName,
            reportName: reportName,
            reportDescription: reportDescription,
            selectedColumns: selectedFields,
            startDate: selectedDuration === "Custom Range" ? startDate : startDateC,
            endDate: selectedDuration === "Custom Range" ? endDate : endDateC,
        };

        try {
            const response = await customReportRequestData(requestData);
            if (response?.status === 201) {
                setAlertMessage(response?.message);
                setAlertType("success");
                setAlertOpen(true);
                setReportName('');
                setReportDescription('');
                setSelectedReport(null);
                setSelectedColumns({});
                setSelectAll(false);
                setSearchTerm('');
                setColumnSearchTerm('');
                setFinalColumnsSearchTerm('');
                setSelectedDuration(dateRangeArr[0]);
                setStartDate('');
                setEndDate('');
                setIsDateRangeModalOpen(false);
            } else {
                setAlertMessage(response?.error);
                setAlertType("error");
                setAlertOpen(true);
                setIsDateRangeModalOpen(false);
            }
        } catch (error) {
            console.error("Error sending email:", error);
            setAlertMessage((error as Error).message);
            setAlertType("error");
            setAlertOpen(true);
        } finally {
            setIsSendingEmail(false);
        }
    };

    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [previewData, setPreviewData] = useState<any>(null);

    const handlePreview = () => {
        if (!selectedReport) {
            console.error("No report selected");
            return;
        }
        const selectedFields = orderedSelectedColumns.filter(field => selectedColumns[field]);

        const columns = selectedReport.reportWiseColumns.All || selectedReport.reportWiseColumns.all || [];
        const selectedLabels = selectedFields.map(field => {
            const column = columns.find((col: any) =>
                (typeof col === 'object' ? col.field : col) === field
            );
            return typeof column === 'object' ? column.label : column;
        })

        const previewData = {
            date_time: selectedReport.date_time,
            fleet_id: user.customer_id,
            parent_id: user.parentId,
            reportCategory: selectedReport.reportName,
            reportName: reportName,
            reportDescription: reportDescription,
            selectedColumns: selectedLabels,
            startDate: selectedDuration === "Custom Range" ? startDate : startDateC,
            endDate: selectedDuration === "Custom Range" ? endDate : endDateC,
        };

        setPreviewData(previewData);
        setIsPreviewOpen(true);
    };

    const reorder = (list: string[], startIndex: number, endIndex: number) => {
        const result = Array.from(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);
        return result;
    };

    const [orderedSelectedColumns, setOrderedSelectedColumns] = useState<string[]>([]);
    useEffect(() => {
        const selected = Object.entries(selectedColumns)
            .filter(([_, isSelected]) => isSelected)
            .map(([field]) => field);
        setOrderedSelectedColumns(prev => {
            const remaining = prev.filter(field => selected.includes(field));
            const newColumns = selected.filter(field => !prev.includes(field));
            return [...remaining, ...newColumns];
        });
    }, [selectedColumns]);

    const handleDragEnd = (result: any) => {
        if (!result.destination) {
            return;
        }

        if (result.destination.index === result.source.index) {
            return;
        }

        const newOrder = reorder(
            orderedSelectedColumns,
            result.source.index,
            result.destination.index
        );

        setOrderedSelectedColumns(newOrder);
    };

    return (
        <div className='flex flex-col gap-4 w-full'>
            {/* Search Input */}
            <div className="w-full pt-8">
                <button className="text-blue-600 font-medium flex items-center" onClick={() => navigate(-1)}>
                    <ArrowBack className="mr-1" /> Back
                </button>
            </div>
            <div className=' w-full flex lg:flex-row md:flex-row flex-col justify-between gap-4 xs:flex-col items-start'>
                <div className="max-w-max">
                    <div className="dashboard-header-left">
                        <div className="flex flex-col items-start">
                            <Typography variant="h4" fontSize="24px" fontWeight={600} lineHeight='36px'>
                                Report Template
                            </Typography>
                        </div>
                    </div>
                </div>

                <div className="flex gap-4 flex-wrap">
                    <CustomIconButton
                        icon={<img src={eyeIcon} alt="preview" width={16} height={16} className={`${isContinueDisabled ? 'opacity-50' : 'opacity-100'}`} />}
                        text="Preview"
                        background="transparent"
                        variant='outlined'
                        onClick={handlePreview}
                        isDisabled={isContinueDisabled}
                    />
                </div>

            </div>
            <form className='w-full relative py-8' onSubmit={(e) => e.preventDefault()}>
                <div className='flex items-start lg:flex-row md:flex-row flex-col gap-4'>
                    <div className='flex gap-4 lg:w-1/3 md:w-1/2 w-full'>
                        <InputField
                            label="Report Name"
                            name="reportName"
                            value={reportName}
                            onChange={(e) => setReportName(e.target.value)}
                            placeholder="Enter Report Name"
                            type="text"
                            isMandatory={true}
                        />
                    </div>

                    <div className="flex flex-col lg:w-2/3 md:w-1/2 w-full">
                        <Typography variant="body2" fontWeight={600} color="textPrimary" className="self-start flex items-center space-x-1">
                            <span>Report Description</span>
                            <span className="text-red-500" title="Required field">*</span>
                        </Typography>

                        <textarea
                            name="reportDescription"
                            value={reportDescription}
                            // onChange={(e) => setReportDescription(e.target.value)}
                            onChange={(e) => {
                                const sanitizedValue = DOMPurify.sanitize(e.target.value, {
                                    ALLOWED_TAGS: [],
                                    ALLOWED_ATTR: [],
                                });
                                setReportDescription(sanitizedValue);
                            }}
                            placeholder="Enter Description"
                            rows={4}
                            style={{
                                width: '100%',
                                padding: '10px',
                                borderRadius: '8px',
                                border: '1px solid #ccc',
                                fontSize: '14px',
                                outline: 'none',
                                resize: 'vertical',
                                marginTop: '8px',
                                color: '#242424',
                            }}
                        />
                    </div>
                </div>
                <div className='grid lg:grid-cols-3 md:grid-cols-3 grid-cols-1 gap-4 mt-4'>
                    <Box className='flex gap-4 flex-col p-4 rounded-lg border border-[#F0EFF8]' sx={{ bgcolor: 'background.paper' }}>
                        <Typography variant="h4" fontSize="24px" fontWeight={600} lineHeight='36px' sx={(theme) => ({
                            color: theme.palette.text.secondary,
                        })}>
                            Pick a Category
                        </Typography>
                        <div className='f-full relative'>
                            <img src={searchIcon} alt='search-icon' className='absolute left-4 top-[45%] z-10' />
                            <InputField
                                name="reportName"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search"
                                type="text"
                                extraClass='pl-10'
                            />
                        </div>
                        <div className='flex flex-col gap-2 mt-4 max-h-[600px] overflow-y-auto'>
                            {filteredReports && filteredReports.map((report: MasterReport) => (
                                <Box
                                    key={report.reportName}
                                    sx={(theme) => ({
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 2,
                                        border: '1px solid',
                                        borderColor: '#F0EFF8',
                                        p: 4,
                                        borderRadius: 2,
                                        cursor: 'pointer',
                                        ...(selectedReport?.reportName === report.reportName && {
                                            borderColor: theme.palette.selected,
                                            backgroundColor: theme.palette.selected
                                        })
                                    })}
                                    onClick={() => handleReportSelect(report)}
                                >
                                    <Typography variant="body2" fontSize="18px" fontWeight={600} lineHeight='28px'>
                                        {report.reportDisplayName}
                                    </Typography>
                                    <Typography variant="body2" fontSize="16px" fontWeight={400} lineHeight='24px'>
                                        {report.reportDisplayName === 'Manager Report'
                                            ? 'Overview of fleet performance, alerts, and driver activities for managerial insights.'
                                            : report.reportDescription}
                                    </Typography>
                                </Box>
                            ))}
                        </div>
                    </Box>
                    <Box className='flex gap-4 flex-col p-4 rounded-lg border border-[#F0EFF8]' sx={(theme) => ({
                        bgcolor: theme.palette.background.paper
                    })}>
                        <div className='flex justify-between items-center w-full'>
                            <Typography variant="h4" fontSize="24px" fontWeight={600} lineHeight='36px'>
                                Pick Your Column
                            </Typography>
                            <Typography
                                variant="body2"
                                fontSize="14px"
                                fontWeight={600}
                                lineHeight='26px'
                                color="primary"
                                onClick={toggleSelectAll}
                                style={{ cursor: 'pointer' }}
                            >
                                {selectAll ? 'Deselect All' : 'Select All'}
                            </Typography>
                        </div>
                        <div className='f-full relative'>
                            <img src={searchIcon} alt='search-icon' className='absolute left-4 top-[45%] z-10' />
                            <InputField
                                name="columnSearch"
                                value={columnSearchTerm}
                                onChange={(e) => setColumnSearchTerm(e.target.value)}
                                placeholder="Search Columns"
                                type="text"
                                extraClass='pl-10'
                            />
                        </div>
                        {selectedReport && (selectedReport.reportWiseColumns.All || selectedReport.reportWiseColumns.all) ? (
                            <div className='flex flex-col gap-2 mt-4 max-h-[600px] overflow-y-auto'>
                                {filteredColumns.map((column: any) => {
                                    const columnLabel = typeof column === 'object' ? column.label : column;
                                    const columnField = typeof column === 'object' ? column.field : column;

                                    return (
                                        <div key={columnField} className='flex gap-2 border border-[#F0EFF8] p-4 rounded-lg justify-between'>
                                            <Typography variant="body2" fontSize="16px" fontWeight={500} lineHeight='26px'>
                                                {columnLabel}
                                            </Typography>
                                            <label className="relative inline-flex items-center cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    className="sr-only peer"
                                                    checked={selectedColumns[columnField] || false}
                                                    onChange={() => toggleColumn(columnField)}
                                                />
                                                <Box className={`w-12 h-6 flex items-center rounded-full transition-colors duration-300 ease-in-out`}
                                                    sx={(theme) => ({
                                                        backgroundColor: selectedColumns[columnField] ? theme.palette.primary.main : theme.palette.secondary.main
                                                    })}
                                                >
                                                    <div className={`bg-white w-5 h-5 rounded-full shadow-md transform transition-transform duration-300 ${selectedColumns[columnField] ? 'translate-x-6' : 'translate-x-1'}`}></div>
                                                </Box>
                                            </label>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <Typography variant="body2" className="text-gray-500 mt-4">
                                Select a report from the left to see its columns
                            </Typography>
                        )}
                    </Box>
                    <Box className='flex gap-4 flex-col p-4 rounded-lg border border-[#F0EFF8]' sx={(theme) => ({
                        bgcolor: theme.palette.background.paper
                    })}>
                        <div className='flex justify-between items-center w-full mb-2'>
                            <Typography variant="h4" fontSize="24px" fontWeight={600} lineHeight='36px'>
                                Manage Final Columns
                            </Typography>
                            <Typography
                                className='rounded-md px-2 ml-4'
                                variant='body2'
                                fontSize='13px'
                                lineHeight='24px'
                                fontWeight={400}
                                bgcolor='#D9EDE6'
                                color='#219653'
                            >
                                {Object.values(selectedColumns).filter(Boolean).length} Columns
                            </Typography>
                        </div>
                        <Typography
                            variant="body2"
                            fontSize="14px"
                            fontWeight={400}
                            lineHeight='26px'
                            color="secondary"
                        >
                            Note : The columns will be arranged in this specified order.
                        </Typography>
                        <div className='f-full relative'>
                            <img src={searchIcon} alt='search-icon' className='absolute left-4 top-[45%] z-10' />
                            <InputField
                                name="finalColumnsSearch"
                                value={finalColumnsSearchTerm}
                                onChange={(e) => setFinalColumnsSearchTerm(e.target.value)}
                                placeholder="Search Columns"
                                type="text"
                                extraClass='pl-10'
                            />
                        </div>
                        {selectedReport ? (
                            <DragDropContext onDragEnd={handleDragEnd}>
                                <Droppable droppableId="selectedColumns">
                                    {(provided) => (
                                        <div
                                            {...provided.droppableProps}
                                            ref={provided.innerRef}
                                            className='flex flex-col gap-2 mt-4 max-h-[600px] overflow-y-auto'
                                        >
                                            {orderedSelectedColumns
                                                .filter(field => selectedColumns[field])
                                                .filter(field =>
                                                    filteredColumns.find((column) => column.field === field)?.label
                                                        .toLowerCase()
                                                        .includes(finalColumnsSearchTerm.toLowerCase())
                                                )
                                                .map((field, index) => {
                                                    const column = filteredColumns.find((col) => col.field === field);
                                                    if (!column) return null;

                                                    return (
                                                        <Draggable key={field} draggableId={field} index={index}>
                                                            {(provided) => (
                                                                <div
                                                                    ref={provided.innerRef}
                                                                    {...provided.draggableProps}
                                                                    {...provided.dragHandleProps}
                                                                    className='flex gap-2 border border-[#F0EFF8] bg-[#F0EFF8] p-2 rounded-lg justify-between items-center'
                                                                >
                                                                    <div className='flex items-center gap-2'>
                                                                        <img src={dragIcon} alt='drag-handle' className='cursor-move' />
                                                                        <Typography variant="body2" fontSize="16px" fontWeight={500} lineHeight='26px'>
                                                                            {column.label}
                                                                        </Typography>
                                                                    </div>
                                                                    <IconButton
                                                                        onClick={() => toggleColumn(field)}
                                                                        size="small"
                                                                        sx={{
                                                                            color: 'error.main',
                                                                            '&:hover': {
                                                                                backgroundColor: 'rgba(255, 0, 0, 0.1)'
                                                                            }
                                                                        }}
                                                                    >
                                                                        <img src={deleteIcon} alt='delete-icon' />
                                                                    </IconButton>
                                                                </div>
                                                            )}
                                                        </Draggable>
                                                    );
                                                })}
                                            {provided.placeholder}
                                            {orderedSelectedColumns.filter(field => selectedColumns[field]).length === 0 && (
                                                <Typography variant="body2" className="text-gray-500 mt-4">
                                                    No columns selected. Select columns from "Pick Your Column" section.
                                                </Typography>
                                            )}
                                        </div>
                                    )}
                                </Droppable>
                            </DragDropContext>
                        ) : (
                            <Typography variant="body2" className="text-gray-500 mt-4">
                                Select a report from the left to see its columns
                            </Typography>
                        )}
                    </Box>
                </div>
                <div className='flex justify-end items-center w-full mt-4 gap-4'>
                    {!isContinueDisabled &&
                        <Buttons
                            text="Save as New Template"
                            onClick={(e) => {
                                e.preventDefault();
                                console.log('data-saved-template');
                            }}
                            disabled={false}
                            fullWidth={false}
                            variant='outlined'
                        />
                    }

                    <Buttons
                        text="Continue"
                        onClick={handleContinue}
                        disabled={isContinueDisabled}
                        fullWidth={false}
                    />
                </div>
                {isDateRangeModalOpen &&
                    <div className='w-full h-full fixed left-0 right-0 mx-auto top-0 flex z-[999]'>
                        <div className='fixed w-full h-full bg-black opacity-50 top-0 left-0 z-[999]'></div>
                        <Box
                            className='absolute left-0 right-0 mx-auto z-[1000] top-1/2 transform -translate-y-1/2 rounded-xl w-full max-w-[480px] h-[600px] p-4'
                            sx={{
                                width: 480,
                                borderRadius: '12px',
                                bgcolor: 'background.paper',
                                boxShadow: 24,
                                p: 4,
                                overflow: 'hidden'
                            }}
                        >
                            <Typography variant='h4' fontWeight={600} fontSize='24px' lineHeight='36px'>
                                Fleet Utilization Report
                            </Typography>
                            <Typography variant='body2' fontWeight={400} color='secondary' fontSize='14px' lineHeight='24px'>
                                Select a date range for which you want to generate your report
                            </Typography>
                            <Typography variant='h4' fontWeight={600} fontSize='14px' lineHeight='26px'>
                                Date Range
                            </Typography>
                            <Box>
                                <div className="flex flex-col gap-4 mt-4">
                                    <div className="grid grid-cols-1 gap-2">
                                        <FormControl component="fieldset" fullWidth>
                                            <RadioGroup
                                                aria-label="date-range"
                                                name="dateRange"
                                                value={selectedDuration}
                                                onChange={(e) => {
                                                    const option = e.target.value;
                                                    setSelectedDuration(option);
                                                    if (option !== "Custom Range") {
                                                        const { startDate: newStartDate, endDate: newEndDate } = getDateRange(option);
                                                        setStartDate(newStartDate ?? '');
                                                        setEndDate(newEndDate ?? '');
                                                    }
                                                }}
                                                sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}
                                            >
                                                {["Last 7 Days", "Last 15 Days", "Last 30 Days", "Custom Range"].map((option) => (
                                                    <FormControlLabel
                                                        key={option}
                                                        value={option}
                                                        control={<Radio />}
                                                        label={option}
                                                        sx={{
                                                            '& .MuiFormControlLabel-label': {
                                                                color: '#5547DD',
                                                                fontWeight: '700',
                                                            },
                                                            margin: 0,
                                                            padding: '8px 12px',
                                                            border: '1px solid #E5E7EB',
                                                            borderRadius: '6px',
                                                            backgroundColor: selectedDuration === option ? '#F3F4F6' : 'transparent',
                                                        }}
                                                    />
                                                ))}
                                            </RadioGroup>
                                        </FormControl>
                                    </div>

                                    {selectedDuration === "Custom Range" && (
                                        <>
                                            <div className="grid grid-cols-1 gap-4 mt-2">
                                                <div className="flex gap-4 sm:flex-row flex-col">
                                                    <div className="flex-1">
                                                        <DateField
                                                            id="fromDate"
                                                            label="From Date"
                                                            name="departureDate"
                                                            value={startDate}
                                                            onChange={(value: string | null) => setStartDate(value ?? '')}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="flex gap-4 sm:flex-row flex-col">
                                                    <div className="flex-1">
                                                        <DateField
                                                            id="toDate"
                                                            label="To Date"
                                                            name="arrivalDate"
                                                            value={endDate}
                                                            onChange={(value: string | null) => setEndDate(value ?? '')}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            {!isDateRangeValid && (
                                                <Typography color="error" variant="body2">
                                                    Please select valid date range (From date must be before To date)
                                                </Typography>
                                            )}
                                        </>
                                    )}

                                    {/* {selectedDuration !== "Custom Range" && (
                                        <div className="mt-4 p-3 bg-blue-50 rounded-md">
                                            <Typography variant="body2">
                                                Selected Range: {startDateC} to {endDateC}
                                            </Typography>
                                        </div>
                                    )} */}
                                </div>
                            </Box>

                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                mt: 2,
                                py: 2,
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                px: 2
                            }}>
                                <Button
                                    onClick={() => setIsDateRangeModalOpen(false)}
                                    variant="outlined"
                                    sx={{ mr: 2 }}
                                >
                                    Back
                                </Button>
                                <Button
                                    onClick={handleApplyDateRange}
                                    variant="contained"
                                    disabled={
                                        selectedDuration === "Custom Range" &&
                                        (!isDateRangeValid || isSendingEmail)
                                    }
                                >
                                    {isSendingEmail ? "Generating..." : "Generate"}
                                </Button>
                            </Box>
                        </Box>
                    </div>
                }

                {isPreviewOpen && previewData && (
                    <CustomReportPreview
                        data={previewData}
                        onClose={() => setIsPreviewOpen(false)}
                    />
                )}
                <AlertToast message={alertMessage} type={alertType} open={alertOpen} onClose={() => setAlertOpen(false)} />
            </form>
        </div>
    );
}

